import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import type {
  UnifiedEvent,
  BusinessSummary,
  EventFilters,
  EventSource,
  ProcessingStatus,
  ProcessingAttempt,
} from "./types";
import { presetToFromTimestamp } from "./types";
import { storeNotificationLabel } from "./store_notification_labels";

const client = generateClient<Schema>();

// ── Secondary-index helpers ───────────────────────────────────────────────────
// Amplify Gen 2 generates listBy* methods from secondary indexes at runtime but
// does not expose them in the generated TypeScript types. We access them via a
// narrowly-scoped cast, mirroring the pattern in the backend Lambda handlers.

type ListByResult<T> = Promise<{
  data: T[];
  nextToken?: string | null;
  errors?: { message: string }[];
}>;

interface ListByOpts {
  sortDirection?: "ASC" | "DESC";
  limit?: number;
  nextToken?: string;
  filter?: Record<string, unknown>;
}

interface GsiKey {
  account: string;
  created?: { ge?: number; le?: number };
}

function connectedEventsByAccount(
  key: GsiKey,
  opts: ListByOpts,
): ListByResult<Schema["StripeConnectedEvent"]["type"]> {
  const model = client.models.StripeConnectedEvent as unknown as {
    listStripeConnectedEventByAccountAndCreated: (
      key: GsiKey,
      opts: ListByOpts,
    ) => ListByResult<Schema["StripeConnectedEvent"]["type"]>;
  };
  return model.listStripeConnectedEventByAccountAndCreated(key, opts);
}

function businessProfilesByStripeAccount(
  stripeConnectedAccountId: string,
): ListByResult<Schema["BusinessProfile"]["type"]> {
  const model = client.models.BusinessProfile as unknown as {
    listBusinessProfileByStripeConnectedAccountId: (key: {
      stripeConnectedAccountId: string;
    }) => ListByResult<Schema["BusinessProfile"]["type"]>;
  };
  return model.listBusinessProfileByStripeConnectedAccountId({
    stripeConnectedAccountId,
  });
}

// ── In-memory cache for account → merchant resolution ────────────────────────

const merchantCache = new Map<string, BusinessSummary | null>();

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Amplify Gen 2 `a.json()` fields come back from DynamoDB as JSON strings in
 * some transport paths rather than pre-parsed objects. Defensively parse here
 * so downstream consumers can treat it as a regular object.
 */
function parseJsonField(value: unknown): unknown {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

function mapPlatformEvent(raw: Schema["StripeEvent"]["type"]): UnifiedEvent {
  const rawRecord = raw as unknown as Record<string, unknown>;
  return {
    source: "platform",
    id: raw.id,
    type: raw.type,
    created: raw.created,
    account: raw.account ?? null,
    livemode: raw.livemode ?? null,
    apiVersion: raw.api_version ?? null,
    request: raw.request
      ? {
          id: raw.request.id ?? null,
          idempotencyKey: raw.request.idempotency_key ?? null,
        }
      : null,
    data: {
      object: parseJsonField(raw.data?.object ?? null),
      previousAttributes: parseJsonField(raw.data?.previous_attributes ?? null),
    },
    eventSourceType: raw.eventSourceType ?? null,
    country: raw.country ?? null,
    provider: null,
    environment: null,
    processingStatus: (rawRecord.processingStatus as ProcessingStatus) ?? null,
    processingError: (rawRecord.processingError as string) ?? null,
    processedAt: (rawRecord.processedAt as number) ?? null,
    processingAttempts: parseAttempts(rawRecord.processingAttempts),
  };
}

function mapConnectedEvent(
  raw: Schema["StripeConnectedEvent"]["type"],
): UnifiedEvent {
  const rawRecord = raw as unknown as Record<string, unknown>;
  return {
    source: "connected",
    provider: null,
    environment: null,
    id: raw.id,
    type: raw.type,
    created: raw.created,
    account: raw.account ?? null,
    livemode: raw.livemode ?? null,
    apiVersion: raw.api_version ?? null,
    request: raw.request
      ? {
          id: raw.request.id ?? null,
          idempotencyKey: raw.request.idempotency_key ?? null,
        }
      : null,
    data: {
      object: parseJsonField(raw.data?.object ?? null),
      previousAttributes: parseJsonField(raw.data?.previous_attributes ?? null),
    },
    eventSourceType: null,
    country: null,
    processingStatus: (rawRecord.processingStatus as ProcessingStatus) ?? null,
    processingError: (rawRecord.processingError as string) ?? null,
    processedAt: (rawRecord.processedAt as number) ?? null,
    processingAttempts: parseAttempts(rawRecord.processingAttempts),
  };
}

/**
 * Build a filter with only non-key attributes (livemode).
 * Used for GSI queries where `created` is the sort key and must be a key condition.
 */
function buildNonDateFilter(
  filters: EventFilters,
): Record<string, unknown> | undefined {
  if (filters.livemode === "all") return undefined;
  return { livemode: { eq: filters.livemode === "true" } };
}

function buildAmplifyFilter(
  filters: EventFilters,
): Record<string, unknown> | undefined {
  const conditions: Record<string, unknown>[] = [];

  // livemode
  if (filters.livemode !== "all") {
    conditions.push({ livemode: { eq: filters.livemode === "true" } });
  }

  // date range
  const dr = filters.dateRange;
  if (dr.preset === "all") {
    // No lower/upper bound — fetch everything matching other filters.
  } else if (dr.preset !== "custom") {
    const from = presetToFromTimestamp(dr.preset);
    conditions.push({ created: { ge: from } });
  } else {
    // custom has a `range` field
    const customDr = dr as {
      preset: "custom";
      range: { from: number; to: number };
    };
    conditions.push({ created: { ge: customDr.range.from } });
    conditions.push({ created: { le: customDr.range.to } });
  }

  if (conditions.length === 0) return undefined;
  if (conditions.length === 1) return conditions[0];
  return { and: conditions };
}

/**
 * Attempts as a list, whatever shape Amplify returned them in.
 *
 * A JSON list can arrive already parsed or still as a string. Cast rather than
 * parsed, a string passes `?? []` intact and the detail view maps over its
 * *characters* -- one row per character of JSON.
 */
function parseAttempts(value: unknown): ProcessingAttempt[] {
  const parsed =
    typeof value === "string"
      ? (() => {
          try {
            return JSON.parse(value);
          } catch {
            return null;
          }
        })()
      : value;

  return Array.isArray(parsed) ? (parsed as ProcessingAttempt[]) : [];
}

/**
 * Date-range and livemode filters for the store tab.
 *
 * Applied server-side so pagination still means something: filtering after the
 * page was fetched would return short pages and, with the default 24-hour
 * range, silently show the whole notification history.
 *
 * `livemode` is a view over `environment`, and both casings are matched because
 * Apple writes the value its payload carries. Google's `unknown` matches
 * neither, which is correct -- an RTDN carries no environment marker.
 */
function buildStoreFilter(
  filters: EventFilters,
): Record<string, unknown> | undefined {
  const conditions: Record<string, unknown>[] = [];

  const dr = filters.dateRange;
  if (dr.preset !== "all") {
    if (dr.preset !== "custom") {
      conditions.push({
        receivedAt: {
          ge: new Date(presetToFromTimestamp(dr.preset) * 1000).toISOString(),
        },
      });
    } else {
      const custom = dr as {
        preset: "custom";
        range: { from: number; to: number };
      };
      conditions.push({
        receivedAt: {
          ge: new Date(custom.range.from * 1000).toISOString(),
          le: new Date(custom.range.to * 1000).toISOString(),
        },
      });
    }
  }

  if (filters.livemode !== "all") {
    const names =
      filters.livemode === "true"
        ? ["production", "Production"]
        : ["sandbox", "Sandbox"];
    conditions.push({
      or: names.map((environment) => ({ environment: { eq: environment } })),
    });
  }

  if (conditions.length === 0) return undefined;
  return conditions.length === 1 ? conditions[0] : { and: conditions };
}

// ── Repository ────────────────────────────────────────────────────────────────

/**
 * Projects a `StoreNotification` onto the shape the inspector already renders.
 *
 * The Stripe-only fields are `null` rather than invented. A store notification
 * has no API version, no idempotency key and no `previous_attributes`, and
 * filling them with plausible values would make the inspector lie about what the
 * store sent.
 *
 * `livemode` is derived from `environment` because that is the question the
 * column actually asks. It stays `null` for Google's `unknown`, which is a real
 * answer: an RTDN carries no environment marker, so the row genuinely does not
 * know yet — that is settled later, from `testPurchase` on the re-fetch.
 */
function mapStoreNotification(
  raw: Schema["StoreNotification"]["type"],
): UnifiedEvent {
  const rawRecord = raw as unknown as Record<string, unknown>;
  const environment = raw.environment ?? null;
  // Apple stores the value exactly as its payload spells it -- `Sandbox` /
  // `Production` -- while Google stores `unknown`. Compared verbatim, every
  // Apple row fell through to `null` and the column read "unknown" for rows
  // whose environment was never in doubt.
  const normalized = environment?.toLowerCase() ?? null;

  return {
    source: "store",
    id: raw.id,
    type: storeNotificationLabel(raw.provider, raw.notificationType),
    created: Math.floor(new Date(raw.receivedAt).getTime() / 1000),
    account: null,
    livemode:
      normalized === "production"
        ? true
        : normalized === "sandbox"
          ? false
          : null,
    apiVersion: null,
    request: null,
    data: {
      object: parseJsonField(raw.rawPayload ?? null),
      previousAttributes: null,
    },
    eventSourceType: raw.subtype ?? null,
    country: null,
    provider: raw.provider ?? null,
    environment,
    processingStatus: (rawRecord.processingStatus as ProcessingStatus) ?? null,
    processingError: (rawRecord.processingError as string) ?? null,
    processedAt: raw.processedAt
      ? Math.floor(new Date(raw.processedAt).getTime() / 1000)
      : null,
    processingAttempts: parseAttempts(rawRecord.processingAttempts),
  };
}

export interface ListOpts {
  filters: EventFilters;
  limit: number;
  nextToken?: string | null;
}

export interface ConnectedListOpts extends ListOpts {
  account?: string | null;
}

export interface ListResult<T> {
  items: T[];
  nextToken: string | null;
}

export class EventsRepository {
  async listPlatform(opts: ListOpts): Promise<ListResult<UnifiedEvent>> {
    const filter = buildAmplifyFilter(opts.filters);
    // Cast the whole call opts through unknown to avoid deep Amplify type complexity
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Amplify ModelFilter is too deeply complex to type-safely construct
    const listOpts: any = {
      limit: opts.limit,
      nextToken: opts.nextToken ?? undefined,
      filter,
    };
    const { data, nextToken, errors } =
      await client.models.StripeEvent.list(listOpts);
    if (errors?.length) throw new Error(errors[0].message);
    return {
      items: (data ?? []).map(mapPlatformEvent),
      nextToken: nextToken ?? null,
    };
  }

  /**
   * Store notifications, newest first.
   *
   * Sorted client-side after the fetch. `StoreNotification` carries no index on
   * `receivedAt` — the model deliberately has none, at roughly 36k rows a year —
   * so there is no key condition to push this down to DynamoDB.
   */
  async listStore(opts: ListOpts): Promise<ListResult<UnifiedEvent>> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Amplify ModelFilter is too deeply complex to type-safely construct
    const listOpts: any = {
      limit: opts.limit,
      nextToken: opts.nextToken ?? undefined,
      filter: buildStoreFilter(opts.filters),
    };
    const { data, nextToken, errors } =
      await client.models.StoreNotification.list(listOpts);
    if (errors?.length) throw new Error(errors[0].message);

    return {
      items: (data ?? [])
        .map(mapStoreNotification)
        .sort((a, b) => b.created - a.created),
      nextToken: nextToken ?? null,
    };
  }

  async listConnected(
    opts: ConnectedListOpts,
  ): Promise<ListResult<UnifiedEvent>> {
    if (opts.account) {
      // Use account+created GSI. `created` is the sort key — it MUST be passed
      // as a key condition, not as a FilterExpression (DynamoDB rejects sort key
      // attributes in FilterExpressions on GSI queries).
      const gsiKey: GsiKey = { account: opts.account };
      const dr = opts.filters.dateRange;
      if (dr.preset !== "all") {
        if (dr.preset !== "custom") {
          gsiKey.created = { ge: presetToFromTimestamp(dr.preset) };
        } else {
          const customDr = dr as {
            preset: "custom";
            range: { from: number; to: number };
          };
          gsiKey.created = { ge: customDr.range.from, le: customDr.range.to };
        }
      }
      // Non-key filters (livemode) stay as FilterExpression
      const filter = buildNonDateFilter(opts.filters);
      const { data, nextToken, errors } = await connectedEventsByAccount(
        gsiKey,
        {
          sortDirection: "DESC",
          limit: opts.limit,
          nextToken: opts.nextToken ?? undefined,
          filter,
        },
      );
      if (errors?.length) throw new Error(errors[0].message);
      return {
        items: (data ?? []).map(mapConnectedEvent),
        nextToken: nextToken ?? null,
      };
    }

    const filter = buildAmplifyFilter(opts.filters);
    // Cast the whole call opts through unknown to avoid deep Amplify type complexity
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Amplify ModelFilter is too deeply complex to type-safely construct
    const listOpts: any = {
      limit: opts.limit,
      nextToken: opts.nextToken ?? undefined,
      filter,
    };
    const { data, nextToken, errors } =
      await client.models.StripeConnectedEvent.list(listOpts);
    if (errors?.length) throw new Error(errors[0].message);
    return {
      items: (data ?? []).map(mapConnectedEvent),
      nextToken: nextToken ?? null,
    };
  }

  async get(source: EventSource, id: string): Promise<UnifiedEvent | null> {
    if (source === "store") {
      const { data, errors } = await client.models.StoreNotification.get({
        id,
      });
      if (errors?.length) throw new Error(errors[0].message);
      return data ? mapStoreNotification(data) : null;
    }
    if (source === "platform") {
      const { data, errors } = await client.models.StripeEvent.get({ id });
      if (errors?.length) throw new Error(errors[0].message);
      return data ? mapPlatformEvent(data) : null;
    }
    const { data, errors } = await client.models.StripeConnectedEvent.get({
      id,
    });
    if (errors?.length) throw new Error(errors[0].message);
    return data ? mapConnectedEvent(data) : null;
  }

  async replayEvent(
    source: "platform" | "connected",
    eventId: string,
  ): Promise<{ success: boolean; error?: string }> {
    const { data, errors } = await client.mutations.ReplayStripeEvent({
      input: {
        eventId,
        eventTable: source,
      },
    });
    if (errors?.length) throw new Error(errors[0].message);
    if (!data?.success) {
      return { success: false, error: data?.error ?? "Unknown error" };
    }
    return { success: true };
  }

  /**
   * Resolves a Stripe connected account ID to a BusinessSummary.
   * Results are cached in-memory per session.
   * Network failures and no-match both return null (silent degradation).
   */
  async resolveAccountToMerchant(
    account: string,
  ): Promise<BusinessSummary | null> {
    if (merchantCache.has(account)) {
      return merchantCache.get(account) ?? null;
    }

    try {
      const { data, errors } = await businessProfilesByStripeAccount(account);
      if (errors?.length || !data?.length) {
        merchantCache.set(account, null);
        return null;
      }
      const bp = data[0];
      const summary: BusinessSummary = {
        id: bp.id,
        businessName: bp.businessName,
        stripeConnectedAccountId: bp.stripeConnectedAccountId ?? null,
      };
      merchantCache.set(account, summary);
      return summary;
    } catch {
      // Silently degrade — never block the page
      merchantCache.set(account, null);
      return null;
    }
  }
}
